import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import { DateRange } from "../../models/common/DateRange";
import {
    getViewSettingsSafely,
    UserViewSettings,
    ViewIdentifiable,
    ViewIdentifier
} from "../../models/common/ViewSettings";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import { Timesheet } from "../../models/timesheet/Timesheet";
import {catchError, mergeMap, switchMap, map} from "rxjs/operators";
import { Observable, of } from "rxjs";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import { ajax } from "rxjs/ajax";
import {eventNotificationError} from "../common/EventNotificationActions";
import {getUrlParamsFromViewSettings} from "../../config/RouteConfig";
import {calendarDeleteFilter, calendarTimePeriodFilter} from "../../models/calendar/CalendarStandardFilters";
import {ActiveDataFilter} from "../../models/common/DataFilter";
import {Identifier} from "../../models/common/Identifier";
import {Workday} from "../../models/calendar/Workday";
import {WorkingHours} from "../../models/timesheet/WorkingHours";
import {Holiday} from "../../models/calendar/Holiday";


export type CalendarViewActionTypes = FetchCalendarEntriesActionTypes
    | FetchHolidaysActionTypes
    | FetchMandatoryWorkhoursActionTypes
    | FetchWorkdaysActionTypes

export interface FetchCalendarEntriesAction extends ViewIdentifiable, ReduxAction<typeof FETCH_CALENDAR_ENTRIES > {
    dateRange: DateRange
}
export interface FetchCalendarEntriesSuccessAction extends ViewIdentifiable, ReduxAction<typeof FETCH_CALENDAR_ENTRIES_SUCCESS > {
    payload: ApiReturnType< Timesheet[] >
    dateRange: DateRange
}
export interface FetchCalendarEntriesErrorAction extends ViewIdentifiable, ErrorAction<typeof FETCH_CALENDAR_ENTRIES_ERROR > {}

export type FetchCalendarEntriesActionTypes = FetchCalendarEntriesAction
    | FetchCalendarEntriesSuccessAction
    | FetchCalendarEntriesErrorAction

export const FETCH_CALENDAR_ENTRIES = "FETCH_CALENDAR_ENTRIES";
export const FETCH_CALENDAR_ENTRIES_SUCCESS = "FETCH_CALENDAR_ENTRIES_SUCCESS";
export const FETCH_CALENDAR_ENTRIES_ERROR = "FETCH_CALENDAR_ENTRIES_ERROR";

export function fetchCalendarEntries( viewIdentifier: ViewIdentifier, dateRange: DateRange ): FetchCalendarEntriesAction {
    return { dateRange, viewIdentifier, type: FETCH_CALENDAR_ENTRIES };
}
export function fetchCalendarEntriesSuccess(
    viewIdentifier: ViewIdentifier,
    dateRange: DateRange,
    payload: ApiReturnType<Timesheet[]>
): FetchCalendarEntriesSuccessAction {
    return { viewIdentifier, dateRange, type: FETCH_CALENDAR_ENTRIES_SUCCESS, payload };
}
export function fetchCalendarEntriesError( viewIdentifier: ViewIdentifier, error: any ): FetchCalendarEntriesErrorAction {
    return { viewIdentifier, type: FETCH_CALENDAR_ENTRIES_ERROR, error };
}

export function fetchCalendarEntriesEpic(
    action$: Observable< FetchCalendarEntriesAction >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( FETCH_CALENDAR_ENTRIES ) ,
        switchMap( action => {
            const path = `${ APP_CONFIG.baseWeb }timesheet/`;
            const settings = getViewSettingsSafely( action.viewIdentifier, store$ );

            // push standard calendar filters to show only non deleted entries
            // and matching the time period chosen
            const standardFilters: ActiveDataFilter<any>[] = [
                calendarDeleteFilter()
            ];
            calendarTimePeriodFilter( action.dateRange ).forEach( _filter => standardFilters.push( _filter ) );

            const adjustedViewSettings: UserViewSettings = {
                ...settings,
                numberOfEntriesPerPage: -1,
                activeFilters: [
                    ...settings.activeFilters,
                    ...standardFilters
                ]
            };


            const requestURI = `${ path }${  getUrlParamsFromViewSettings( adjustedViewSettings ) }`;

            return ajax( APP_CONFIG.getRequestObject( requestURI ) ).pipe(
                mergeMap( response => of(
                    fetchCalendarEntriesSuccess(
                            action.viewIdentifier,
                            action.dateRange,
                            response.response as ApiReturnType< Timesheet[] >
                        )
                    )
                ),
                catchError( error => of(
                    fetchCalendarEntriesError( action.viewIdentifier, error ),
                    eventNotificationError(
                        error,
                        store$,
                        'calendar',
                        'fetchCalendarEntries'
                    )
                ) )
            );
        })
    );
}

/**
 * fetch holidays
 */
export interface FetchHolidaysAction extends ReduxAction<typeof FETCH_HOLIDAYS > {
    userId: Identifier<number>
    dateRange: DateRange
}
export interface FetchHolidaysSuccessAction extends ReduxAction<typeof FETCH_HOLIDAYS_SUCCESS > {
    userId: Identifier<number>
    payload: Holiday[]
    dateRange: DateRange
}
export interface FetchHolidaysErrorAction extends ErrorAction<typeof FETCH_HOLIDAYS_ERROR > {}

export type FetchHolidaysActionTypes = FetchHolidaysAction
    | FetchHolidaysSuccessAction
    | FetchHolidaysErrorAction

export const FETCH_HOLIDAYS = "FETCH_HOLIDAYS";
export const FETCH_HOLIDAYS_SUCCESS = "FETCH_HOLIDAYS_SUCCESS";
export const FETCH_HOLIDAYS_ERROR = "FETCH_HOLIDAYS_ERROR";

export function fetchHolidays( userId: Identifier< number >, dateRange: DateRange ): FetchHolidaysAction {
    return { dateRange, userId, type: FETCH_HOLIDAYS };
}
export function fetchHolidaysSuccess(
    userId: Identifier< number >,
    dateRange: DateRange,
    payload: Holiday[]
): FetchHolidaysSuccessAction {
    return { userId, dateRange, type: FETCH_HOLIDAYS_SUCCESS, payload };
}
export function fetchHolidaysError( error: any ): FetchHolidaysErrorAction {
    return { type: FETCH_HOLIDAYS_ERROR, error };
}

export function fetchHolidaysEpic(
    action$: Observable< FetchHolidaysAction >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( FETCH_HOLIDAYS ) ,
        switchMap( action => {
            const path = `${ APP_CONFIG.baseWeb }employee/contract/holidays/${ action.userId.rawValue }`;

            const requestURI = `${ path }?startAt=${ action.dateRange.start.getTime() }&finishAt=${ action.dateRange.endInclusive.getTime() }`;

            return ajax( APP_CONFIG.getRequestObject( requestURI ) ).pipe(
                map( response => fetchHolidaysSuccess(
                    action.userId,
                    action.dateRange,
                    response.response as Holiday[]
                ) ),
                catchError( error => of(
                    fetchHolidaysError( error ),
                    eventNotificationError(
                        error,
                        store$,
                        'calendar',
                        'fetchHolidays'
                    )
                ) )
            );
        })
    );
}

/**
 * fetch workdays
 */
export interface FetchWorkdaysAction extends ReduxAction<typeof FETCH_WORKDAYS > {
    userId: Identifier<number>
}
export interface FetchWorkdaysSuccessAction extends ReduxAction<typeof FETCH_WORKDAYS_SUCCESS > {
    userId: Identifier<number>
    payload: Workday[]
}
export interface FetchWorkdaysErrorAction extends ErrorAction<typeof FETCH_WORKDAYS_ERROR > {}

export type FetchWorkdaysActionTypes = FetchWorkdaysAction
    | FetchWorkdaysSuccessAction
    | FetchWorkdaysErrorAction

export const FETCH_WORKDAYS = "FETCH_WORKDAYS";
export const FETCH_WORKDAYS_SUCCESS = "FETCH_WORKDAYS_SUCCESS";
export const FETCH_WORKDAYS_ERROR = "FETCH_WORKDAYS_ERROR";

export function fetchWorkdays( userId: Identifier< number > ): FetchWorkdaysAction {
    return { userId, type: FETCH_WORKDAYS };
}
export function fetchWorkdaysSuccess(
    userId: Identifier< number >,
    payload: Workday[]
): FetchWorkdaysSuccessAction {
    return { userId, type: FETCH_WORKDAYS_SUCCESS, payload };
}
export function fetchWorkdaysError( error: any ): FetchWorkdaysErrorAction {
    return { type: FETCH_WORKDAYS_ERROR, error };
}

export function fetchWorkdaysEpic(
    action$: Observable< FetchWorkdaysAction >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( FETCH_WORKDAYS ) ,
        switchMap( action => {
            const path = `${ APP_CONFIG.baseWeb }employee/contract/workdays/${ action.userId.rawValue }`;

            return ajax( APP_CONFIG.getRequestObject( path ) ).pipe(
                map( response => fetchWorkdaysSuccess(
                    action.userId,
                    response.response as Workday[]
                ) ),
                catchError( error => of(
                    fetchWorkdaysError( error ),
                    eventNotificationError(
                        error,
                        store$,
                        'calendar',
                        'fetchWorkdays'
                    )
                ) )
            );
        })
    );
}

/**
 * fetch mandatory hours
 */
export interface FetchMandatoryWorkhoursAction extends ReduxAction<typeof FETCH_MANDATORY_WORKHOURS > {
    userId: Identifier<number>
    dateRange: DateRange
}
export interface FetchMandatoryWorkhoursSuccessAction extends ReduxAction<typeof FETCH_MANDATORY_WORKHOURS_SUCCESS > {
    userId: Identifier<number>
    payload: WorkingHours
    dateRange: DateRange
}
export interface FetchMandatoryWorkhoursErrorAction extends ErrorAction<typeof FETCH_MANDATORY_WORKHOURS_ERROR > {}

export type FetchMandatoryWorkhoursActionTypes = FetchMandatoryWorkhoursAction
    | FetchMandatoryWorkhoursSuccessAction
    | FetchMandatoryWorkhoursErrorAction

export const FETCH_MANDATORY_WORKHOURS = "FETCH_MANDATORY_WORKHOURS";
export const FETCH_MANDATORY_WORKHOURS_SUCCESS = "FETCH_MANDATORY_WORKHOURS_SUCCESS";
export const FETCH_MANDATORY_WORKHOURS_ERROR = "FETCH_MANDATORY_WORKHOURS_ERROR";

export function fetchMandatoryWorkhours( userId: Identifier< number >, dateRange: DateRange ): FetchMandatoryWorkhoursAction {
    return { dateRange, userId, type: FETCH_MANDATORY_WORKHOURS };
}
export function fetchMandatoryWorkhoursSuccess(
    userId: Identifier< number >,
    dateRange: DateRange,
    payload: WorkingHours
): FetchMandatoryWorkhoursSuccessAction {
    return { userId, dateRange, type: FETCH_MANDATORY_WORKHOURS_SUCCESS, payload };
}
export function fetchMandatoryWorkhoursError( error: any ): FetchMandatoryWorkhoursErrorAction {
    return { type: FETCH_MANDATORY_WORKHOURS_ERROR, error };
}

export function fetchMandatoryWorkhoursEpic(
    action$: Observable< FetchMandatoryWorkhoursAction >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( FETCH_MANDATORY_WORKHOURS ) ,
        switchMap( action => {
            const path = `${ APP_CONFIG.baseWeb }employee/contract/hours/mandatory/${ action.userId.rawValue }`;

            const requestURI = `${ path }?startAt=${ action.dateRange.start.getTime() }&finishAt=${ action.dateRange.endInclusive.getTime() }`;

            return ajax( APP_CONFIG.getRequestObject( requestURI ) ).pipe(
                map( response => fetchMandatoryWorkhoursSuccess(
                    action.userId,
                    action.dateRange,
                    response.response as WorkingHours
                ) ),
                catchError( error => of(
                    fetchMandatoryWorkhoursError( error ),
                    eventNotificationError(
                        error,
                        store$,
                        'calendar',
                        'fetchMandatoryWorkhours'
                    )
                ) )
            );
        })
    );
}